import React, { useEffect, useRef, useState } from 'react';
import { AuthConsumer } from 'contexts/auth-context';
import CQGlobalHeader from 'components/cq-global-header';
import CQModule from 'components/cq-module';
import BrandBand from '@salesforce/design-system-react/components/brand-band'; 
import PageHeader from '@salesforce/design-system-react/components/page-header';
import Icon from '@salesforce/design-system-react/components/icon';
import { useTranslation } from 'react-i18next';
import { CQTimeline } from 'components/cq-timeline';
import CQStencil from 'components/cq-stencil';
import { Button, IconSettings, Modal, PageHeaderControl} from '@salesforce/design-system-react';
import CQCurrentOrgInfoModal from 'components/cq-current-org-info-modal';
import CQSummaryReport from 'components/cq-summary-report';
import DefaultTaskEngine from 'tasks/task-engine';
import { IPastRun } from 'interfaces/IHistory.interface';
import { SFConnection, SfConnector } from 'common/sf-connector';
import { IApplicableModules } from 'interfaces/IUpgradeSchema.interface';
import { CQAppConstant } from 'app-constants';
import DefaultStorage from 'common/storage';
import { CQMgmtServiceProvider } from 'services/cq-mgmt.service';
import { IOrgInfo } from 'interfaces/IModal.interface';
import { useNavigate } from 'react-router-dom';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import { isRefreshModule } from 'common/module.utils';

let applicableModules:{
    allModules: IApplicableModules[],
    lastFailedModule: IApplicableModules,
    lastRun:IPastRun,
    lastSkippedModule: IApplicableModules,
    currentlyActiveModule: IApplicableModules | null;
    daVersion?: string;
    daReleaseDate?: string;
}

/**
 * Loads application data by fetching initialization data from the server.
 * Attempts to retrieve applicable modules via an API call and restores default storage.
 * 
 * @async
 * @function loadData
 * @returns {Promise<void>}
 * @throws {Error} Logs any errors that occur during data fetching to the console.
 * 
 * @remarks
 * This function uses Promise.all() to execute multiple async operations concurrently,
 * but only processes the response from the initialization endpoint.
 */
async function loadData() {
    try {
        const [dataResponse] = await Promise.all([
                SFConnection.getAxiosInstance().get(CQAppConstant.INITIALIZE),
                CQMgmtServiceProvider.checkInstalledPackagesWithVersions(),
                DefaultStorage.restore()
            ]);

        applicableModules = dataResponse.data;

    } catch (error) {
        console.error('[loadData] failed:', error);
        throw error;
    }
}
  


function CQMainScreen() {
    const {t} = useTranslation();
    const authContext = AuthConsumer();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [viewHistoryButton, setViewHistoryButton] = useState<boolean>(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
    const [currentModule, setCurrentModule] = useState<string>(CQAppConstant.MODULE_TYPES.UPDATES);
    const [isEmptyEvent, setIsEmptyEvent] = useState<boolean>(false);
    const [orgInfo, setOrgInfo] = useState<IOrgInfo>({ name: '', isSandbox: '', userName: '', profileName: '', fullName: '' });
    const [canAccessPushPortal, setCanAccessPushPortal] = useState<boolean>(false);
    const [lastRunCompletedModule, setLastRunCompletedModule] = useState<string>('');
    const [reloadMainScreenTrigger, setReloadMainScreenTrigger] = useState(0);
    // Key to remount CQModule and CQTimeline when data refreshes
    const [moduleKey, setModuleKey] = useState<number>(0);
    const lastProcessedReloadRef = useRef(0);
    // Last run id we reloaded for. Kept in the parent so it survives the child
    // remount a reload causes, preventing the same run from re-triggering reloads.
    const lastNotifiedRunIdRef = useRef<string | null>(null);
    const navigate = useNavigate();

    /**
     * Triggers a reload for a newly started run, deduped by run id so each run
     * start reloads once (children remount on reload and lose their local guards).
     *
     * @param runId - Stable run identifier (e.g. its startedOn timestamp).
     */
    function notifyRunStart(runId: string) {
        if (!runId || runId === lastNotifiedRunIdRef.current) {
            return;
        }
        lastNotifiedRunIdRef.current = runId;
        setReloadMainScreenTrigger(prev => prev + 1);
    }

    // start loading the data if authenticated
    useEffect(() => {
        if (authContext.isAuthenticated) {
            setIsLoading(true);
            loadData().then(() => {
                fetchData();
                fetchOrgAndUserDetails();
                checkPushPortalPermission();
                setIsLoading(false);
            }).catch((error: any) => {
                console.error('[CQMainScreen] loadData failed:', error instanceof Error ? error.message : JSON.stringify(error));
                const message = error?.response?.data?.error || (error instanceof Error ? error.message : CQAppConstant.ERROR_MESSAGES.UNKNOWN_ERROR);
                authContext.logout();
                navigate(`/?error=${encodeURIComponent(`${message} ${CQAppConstant.ERROR_MESSAGES.CONTACT_ADMINISTRATOR}`)}`, { replace: true });
            });
        }
    }, [authContext.isAuthenticated]);

    useEffect(() => {
        fetchData();
    }, [currentModule]);

    // Detect external reload requests and reinitialize data. Uses ref to ensure each trigger value is processed exactly once.
    useEffect(() => {
        async function reinitialize() {
            try {
                await loadData();
                fetchData();
                setModuleKey(key => key + 1);
            } catch (error: any) {
                console.error('[CQMainScreen] reinitialize failed:', error instanceof Error ? error.message : JSON.stringify(error));
                // Do not logout mid-session — user can retry via the Refresh button
            }
        }

        if (reloadMainScreenTrigger !== lastProcessedReloadRef.current) {
            reinitialize();
            lastProcessedReloadRef.current = reloadMainScreenTrigger;
        }
    }, [reloadMainScreenTrigger]);

    /**
     * Fetches the installed packages history using the DefaultTaskEngine.
     * If the history is available and lastFailedModule is empty, it updates the state to show the "View History" button.
     *
     * @async
     * @function fetchData
     * @returns {Promise<void>} A promise that resolves when the data fetching and state update are complete.
     */
    async function fetchData () {
        let history= await DefaultTaskEngine.readInstalledPackagesHistory();
        if (history && !applicableModules?.lastFailedModule) {
            setViewHistoryButton(true);
        } else {
            setViewHistoryButton(false);
        }
    }

/**
 * Fetches the current Salesforce organization and user details from SFConnection
 * and updates the component state with the retrieved information.
 *
 * The function retrieves organization details such as organization name and type
 * (Sandbox or Production), along with user details including username, full name,
 * and profile name.
 *
 * @async
 * @function fetchOrgAndUserDetails
 * @returns {Promise<void>} Resolves once the organization and user details
 * are successfully fetched and the state is updated.
 */
    async function fetchOrgAndUserDetails() {
        try{
            const orgData = await SFConnection.getOrgAndUserDetails();
            if(!orgData) return;
            setOrgInfo({ 
                name: orgData.orgName, 
                isSandbox: orgData.isSandbox ? CQAppConstant.ORG_TYPE.SANDBOX : CQAppConstant.ORG_TYPE.PRODUCTION, 
                userName: orgData.user.username, 
                profileName: orgData.user.profileName, 
                fullName: orgData.user.fullName 
            });
        }catch(error){
            console.error(error)
        }
    }

    /**
     * Checks if the current user has permission to access Push Portal.
     * Sets canAccessPushPortal state based on the permission check result.
     * On error, defaults to hiding the Push Portal button.
     */
    async function checkPushPortalPermission() {
        try {
            const permissions = await PushPortalServiceProvider.getPermissions();
            setCanAccessPushPortal(permissions.canView);
        } catch (error) {
            console.error('Failed to check Push Portal permissions:', error);
            setCanAccessPushPortal(false);
        }
    }

    /**
     * Renders the title section of the main screen.
     *
     * This function returns a JSX element containing a vertical grid layout
     * with a main header and a subheader. The main header displays a localized
     * string for the homepage page header, while the subheader shows the package
     * name and version (or a default "No package" message) along with additional
     * localized organization installation information.
     *
     * @returns {JSX.Element} A JSX element representing the title section.
     */
    function renderTitle() {
        const packageVersion = SfConnector.releaseVersionName ?? CQAppConstant.NO_PACKAGE;
        const userDisplay = `${orgInfo.userName || ''} / ${orgInfo.fullName || ''} / ${orgInfo.profileName || ''}`;

        return (
            <div className="slds-grid slds-grid_vertical">
                <h1>{t("homepage_pageheader")}</h1>

                <h2 className="slds-text-heading_small slds-m-vertical_xx-small">
                    {packageVersion} {t("org_install_info")}
                </h2>

                <p className="slds-text-body_small">
                    {t("org_name")} {orgInfo.name} <br />
                    {t("org_type")} {orgInfo.isSandbox} <br />
                    {t("org_login")} {userDisplay}
                </p>
            </div>
        );
    }

    const onRenderAction = () => {
        const sameModuleCheck = currentModule === lastRunCompletedModule;
        return (
            <PageHeaderControl>
                {lastRunCompletedModule && sameModuleCheck &&( 
                    <Button
                        label={currentModule === CQAppConstant.MODULE_TYPES.UPGRADES ? t("refresh_label", { type: currentModule }) : "Refresh"}
                        variant="brand"
                        iconName="refresh"
                        iconPosition="left"
                        onClick={() => window.location.reload()}
                    />
                )}
                { viewHistoryButton && !isRefreshModule(currentModule) && ( <Button label={t("view_history", {type: currentModule })} variant="brand" onClick={() => setIsReportModalOpen(!isReportModalOpen)} />)}
                { canAccessPushPortal && (
                    <Button
                        label={t("push_portal_pageheader")}
                        variant="brand"
                        iconCategory="utility"
                        iconName="announcement"
                        iconPosition="left"
                        onClick={() => navigate('/push-portal')}
                    />
                )}
                <CQCurrentOrgInfoModal modalOpenState={isModalOpen} toggleModalState={() => setIsModalOpen(!isModalOpen)} packageNameAndVersion = {SfConnector.releaseVersionName} orgInfo={orgInfo} /> 
            </PageHeaderControl>
        );
    }

    return (
        <>
            <IconSettings iconPath="/assets/icons">
            { authContext.isAuthenticated ?
                (isLoading ?
                    <CQStencil /> :
                    <>
                        <CQGlobalHeader></CQGlobalHeader>
                        <div style={{marginTop: "3rem"}}>
                            <BrandBand
                                id="brand-band-lightning-blue"
                                className="slds-p-around_small"
                            >
                                <PageHeader
                                    icon={
                                        <Icon
                                            assistiveText={{
                                                label: 'Home',
                                            }}
                                            category="utility"
                                            name="setup"
                                            colorVariant="warning"
                                            title="Home"
                                        />
                                    }
                                    onRenderActions={onRenderAction}
                                    title={renderTitle()}
                                    variant="object-home"
                                />
                                
                                <div className="slds-grid slds-scrollable_none">
                                    <div className={`slds-col slds-size_${isEmptyEvent ? 12 : 8}-of-12`}>
                                        <div className="slds-box slds-theme_default slds-var-m-top_medium">
                                            <CQModule key={moduleKey} applicableModules={applicableModules} setViewHistoryButton={setViewHistoryButton} setCurrentModule={setCurrentModule} orgInfo={orgInfo} setLastRunCompletedModule={setLastRunCompletedModule} notifyRunStart={notifyRunStart}></CQModule>
                                        </div>
                                    </div>
                                    <div className="slds-col slds-size_4-of-12">
                                        <div className="slds-box slds-theme_default  slds-var-m-left_small slds-var-m-top_medium">
                                            <CQTimeline key={moduleKey} currentModule={currentModule} setIsEmptyEvent={setIsEmptyEvent}></CQTimeline>
                                        </div>
                                    </div>  
                                </div>
                                    
                            </BrandBand>
                        
                        </div>
                    </>
                ) : <></>
            }
            <>
                <Modal
                    isOpen={isReportModalOpen}
                    onRequestClose={ ( ) => setIsReportModalOpen(!isReportModalOpen)}
                    size="medium"
                    footer={[
                        <Button label={t("close")} onClick={ ( ) => setIsReportModalOpen(!isReportModalOpen)} />,
                    ]}
                >
                    <div className='slds-m-around_large'>
                        <CQSummaryReport isViewHistoryClicked={true} currentModule={currentModule}/>
                    </div>
                </Modal>
            </>
            </IconSettings>
        </>
    );
}

export default CQMainScreen;